import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useController, useForm, useWatch } from "react-hook-form";

import Lens from "@mui/icons-material/Lens";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";

import { addDays, format, startOfDay } from "date-fns";

import { useAppDispatch, useAppSelector } from "app/hooks";
import ClinicFormField from "components/clinic-form-field";
import DateFormField from "components/date-form-field/date-form-field.component";
import Dialog from "components/dialog";
import LoadingOverlay from "components/loading-overlay/loading-overlay.component";
import PatientFieldWithSearch from "components/patient-field-with-search";
import SelectProviderSimpleField from "components/select-provider-simple-field/select-provider-simple-field.component";
import { APPOINTMENT_TYPE_SCHEDULE } from "constants/appointment-types";
import { EVENT_TYPE_APPOINTMENT } from "constants/appointments";
import { APPOINTMENT_TYPE_DEFAULT_CHART_NOTE } from "constants/feature-flags";
import { GROUP_AUDIOLOGISTS } from "constants/staff";
import { selectCurrentClinic } from "features/auth/auth-slice";
import useAppointments from "features/scheduling/hooks/use-appointments.hook";
import { STAFF_COLORS } from "features/scheduling/scheduler-side-sheet/filters/staff-filter/staff-search.component";
import { openForm } from "features/scheduling/scheduler-slice";
import useDeferredLoading from "hooks/use-deferred-loading.hook";
import {
  APPOINTMENT_TYPES_QUERY_WITH_DEFAULT_NOTES,
  useGetAppointmentTypesQuery
} from "services/appointment-types";
import { useGetStaffQuery } from "services/staff";
import type { AppointmentType } from "services/types/appointment-types.type";
import type { Clinic } from "services/types/clinics.type";
import type { Patient } from "services/types/patients.type";
import { formatDurationValue } from "utils/common";

import SlotResultsList, {
  type ProviderMeta,
  type ResultsGroupBy,
  SLOT_MIN_HEIGHT,
  formatSlotDayLabel
} from "./slot-results-list.component";
import StartFromPresets, {
  type StartFromChoice,
  startFromDateForMonths
} from "./start-from-presets.component";
import useFindNextAvailable, {
  type FoundSlot,
  MAX_SEARCH_WINDOWS,
  SEARCH_WINDOW_DAYS,
  type SearchCriteria,
  getSlotDurationMinutes
} from "./use-find-next-available.hook";

export const MAX_PROVIDERS = 3;
// Split-view layout: criteria rail on the left, results pane on the right.
// Below the `md` breakpoint the panes stack and the results keep the old
// inline-scroll behavior.
const DIALOG_MIN_WIDTH = 1120;
const CRITERIA_RAIL_WIDTH = 360;
// The 400px floor keeps title + pane + paddings inside a ~614px-tall viewport
// (a 768p laptop at 125% OS scaling) so the dialog body never double-scrolls.
const RESULTS_PANE_HEIGHT = "clamp(400px, calc(100vh - 280px), 640px)";
// Placeholder must match a real slot chip (dot + time + provider + chevron at
// SLOT_MIN_HEIGHT) or the skeleton→results swap reflows the whole pane.
const SKELETON_SLOT_WIDTH = 185;
// Same timing guard as the marketing dashboard overlay: a cached re-search
// resolves silently instead of flashing, and once shown it never blinks out.
export const LOADING_SHOW_DELAY_MS = 250;
export const LOADING_MIN_VISIBLE_MS = 400;

export interface ProviderOption {
  label: string;
  value: string;
}

interface FindNextAvailableFormValues {
  appointment_type: AppointmentType | null;
  providers: ProviderOption[];
  clinic: Clinic | null;
  // Only the picker reads this. The full Patient is kept in component state (`patient`
  // below): as a form value it made FieldPath 49,643 paths and this file took ~10 min to
  // type-check (ENG-2531).
  patient: Pick<Patient, "id"> | null;
  // Custom "Start search from" date; only read while the Custom preset is
  // selected.
  start_from: Date | null;
}

interface ActiveSearch {
  criteria: SearchCriteria;
  providers: ProviderMeta[];
  showSourceBadges: boolean;
}

/**
 * Pre-seeds the criteria rail — used by the checkout entry points (the
 * "Schedule next appointment?" modal and the Current Visit drawer) which
 * already know the patient, follow-up type, and target interval.
 */
export interface FindNextAvailableInitialValues {
  appointmentType?: AppointmentType | null;
  clinic?: Clinic | null;
  patient?: Patient | null;
  providers?: ProviderOption[];
  startFromMonths?: number | null;
  // Runs after the appointment the picked slot seeds is actually saved — lets
  // the caller finish its own flow (e.g. the patient chart returning to the
  // Appointments tab). Receives the patient the slot was picked for, which is
  // the dialog's own field and may differ from the one seeded in. Omitted =>
  // save behaves exactly as before.
  onScheduled?: (patient: Patient | null) => void;
}

interface FindNextAvailableDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly initialValues?: FindNextAvailableInitialValues;
}

function ResultsSkeleton() {
  return (
    <Stack
      spacing={1.5}
      sx={{ py: 1 }}
      data-testid="find-next-available-skeleton"
    >
      {[0, 1, 2].map((group) => (
        <Box key={group}>
          <Skeleton variant="text" width={150} sx={{ mb: 0.5 }} />
          <Stack direction="row" gap={1}>
            <Skeleton
              variant="rounded"
              width={SKELETON_SLOT_WIDTH}
              height={SLOT_MIN_HEIGHT}
            />
            <Skeleton
              variant="rounded"
              width={SKELETON_SLOT_WIDTH}
              height={SLOT_MIN_HEIGHT}
            />
            {group !== 1 && (
              <Skeleton
                variant="rounded"
                width={SKELETON_SLOT_WIDTH}
                height={SLOT_MIN_HEIGHT}
              />
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

export default function FindNextAvailableDialog({
  open,
  onClose,
  initialValues
}: FindNextAvailableDialogProps) {
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const isDefaultChartNoteEnabled = flags[APPOINTMENT_TYPE_DEFAULT_CHART_NOTE];
  const currentClinic = useAppSelector(selectCurrentClinic);
  const { setupAppointmentModal, appointmentFormId } = useAppointments();

  // Args deliberately match AppointmentTypeField's query so RTK Query serves
  // both surfaces from a single cache entry.
  const { data: appointmentTypes = [], isLoading: isLoadingTypes } =
    useGetAppointmentTypesQuery({
      is_active: true,
      is_clinic_schedule: true,
      exclude_type: APPOINTMENT_TYPE_SCHEDULE,
      ...(isDefaultChartNoteEnabled && {
        query: APPOINTMENT_TYPES_QUERY_WITH_DEFAULT_NOTES
      })
    });

  const { control } = useForm<FindNextAvailableFormValues>({
    defaultValues: {
      appointment_type: initialValues?.appointmentType ?? null,
      providers: initialValues?.providers?.slice(0, MAX_PROVIDERS) ?? [],
      // The clinic switcher hydrates in two phases; a partial object is fine
      // here — only `id` (query) and identity (field value) are read.
      clinic: initialValues?.clinic ?? currentClinic ?? null,
      patient: initialValues?.patient ?? null,
      start_from: null
    }
  });

  const {
    field: { value: appointmentType, onChange: onAppointmentTypeChange }
  } = useController({ name: "appointment_type", control });
  const {
    field: { value: providers, onChange: onProvidersChange }
  } = useController({ name: "providers", control });
  // Explicit generics: an inferred name tuple makes tsc relate every Clinic path to
  // every other one, which never finishes (ENG-2531). Type-only, no runtime change.
  const [clinic, customStartDate] = useWatch<
    FindNextAvailableFormValues,
    ["clinic", "start_from"]
  >({
    control,
    name: ["clinic", "start_from"]
  });
  // The picker hands the same object to the form field and to onOptionSelected, and nothing
  // else writes the field, so this always matches it (ENG-2531).
  const [patient, setPatient] = useState<Patient | null>(
    initialValues?.patient ?? null
  );

  const [groupBy, setGroupBy] = useState<ResultsGroupBy>("time");
  const [activeSearch, setActiveSearch] = useState<ActiveSearch | null>(null);
  const [startFromChoice, setStartFromChoice] = useState<StartFromChoice>(
    initialValues?.startFromMonths ?? null
  );

  // Null = search from now (today's behavior). A custom preset with no date
  // picked yet also searches from now rather than blocking the search.
  const startFrom = useMemo(() => {
    if (startFromChoice === "custom") {
      return customStartDate ? startOfDay(new Date(customStartDate)) : null;
    }
    return startFromDateForMonths(startFromChoice);
  }, [startFromChoice, customStartDate]);

  const {
    search,
    reset: resetSearch,
    slots,
    status,
    hasError,
    staffIdsWithoutSlots,
    staffIdsStillSearching,
    searchedThroughDate
  } = useFindNextAvailable();

  // Editing any criterion invalidates what's on screen — the results would
  // otherwise silently describe a different search than the fields show.
  const providersKey = providers.map((provider) => provider.value).join(",");
  const startFromKey = startFrom?.getTime() ?? 0;
  useEffect(() => {
    setActiveSearch(null);
    resetSearch();
  }, [
    appointmentType?.id,
    providersKey,
    clinic?.id,
    startFromKey,
    resetSearch
  ]);

  // Same args as SelectProviderSimpleField builds internally, so RTK Query
  // serves both from a single cache entry. Used to drop selections the
  // dropdown no longer offers — a non-audiologist picked before switching to
  // an audiologists-only type, or a provider who doesn't work at the newly
  // selected clinic — so the search never runs for providers who can't
  // perform the appointment.
  const { currentData: selectableStaff } = useGetStaffQuery({
    is_active: true,
    groupName: appointmentType?.is_audiologists_only
      ? GROUP_AUDIOLOGISTS
      : undefined,
    clinicIds: clinic?.id ? [clinic.id] : undefined
  });

  useEffect(() => {
    // `currentData` (not `data`) so a roster still loading for the new args
    // can never prune against the previous clinic's/group's list.
    if (!selectableStaff) return;
    const selectableIds = new Set(
      selectableStaff.map((staffMember) => String(staffMember.id))
    );
    const validProviders = providers.filter((provider) =>
      selectableIds.has(provider.value)
    );
    if (validProviders.length !== providers.length) {
      onProvidersChange(validProviders);
    }
  }, [selectableStaff, providers, onProvidersChange]);

  const handleProvidersSelected = useCallback(
    (selected: unknown) => {
      const nextProviders = (selected ?? []) as ProviderOption[];
      // Hard cap — selections beyond MAX_PROVIDERS are ignored, the helper
      // text explains the limit.
      if (nextProviders.length > MAX_PROVIDERS) return;
      onProvidersChange(nextProviders);
    },
    [onProvidersChange]
  );

  const canSearch =
    Boolean(appointmentType) &&
    providers.length > 0 &&
    Boolean(clinic?.id) &&
    status !== "searching";

  const handleSearch = useCallback(() => {
    if (!appointmentType || !clinic?.id || providers.length === 0) return;
    const providerMeta: ProviderMeta[] = providers.map((provider, index) => ({
      id: Number(provider.value),
      name: provider.label,
      color: STAFF_COLORS[index % STAFF_COLORS.length]
    }));
    const criteria: SearchCriteria = {
      appointmentType,
      staffMemberIds: providerMeta.map((provider) => provider.id),
      clinicId: clinic.id,
      startFrom
    };
    setActiveSearch({
      criteria,
      providers: providerMeta,
      showSourceBadges: Boolean(appointmentType.limit_to_appointment_blocks)
    });
    search(criteria);
  }, [appointmentType, clinic?.id, providers, search, startFrom]);

  const handleSelectSlot = useCallback(
    (slot: FoundSlot) => {
      if (!activeSearch) return;
      const onScheduled = initialValues?.onScheduled;
      setupAppointmentModal(
        {
          // Seed keys are the event/camelCase shape `setupAppointmentModal`
          // maps into form values — `appointmentType`, NOT `appointment_type`
          // (the snake_case key is silently dropped for appointments).
          type: EVENT_TYPE_APPOINTMENT,
          appointmentType: activeSearch.criteria.appointmentType,
          staffMember: slot.staffMember,
          staffMemberId: slot.staffMember?.id,
          clinic: slot.clinic,
          startDate: slot.startTime,
          endDate: slot.endTime,
          ...(patient ? { patient } : {})
        },
        "add",
        onScheduled ? () => onScheduled(patient ?? null) : undefined
      );
      dispatch(openForm(appointmentFormId));
      onClose();
    },
    [
      activeSearch,
      appointmentFormId,
      dispatch,
      initialValues?.onScheduled,
      onClose,
      patient,
      setupAppointmentModal
    ]
  );

  const showLoadingOverlay = useDeferredLoading(status === "searching", {
    showDelayMs: LOADING_SHOW_DELAY_MS,
    minVisibleMs: LOADING_MIN_VISIBLE_MS
  });

  const durationMinutes = getSlotDurationMinutes(appointmentType);
  const loadingMessages = useMemo(
    () => [
      appointmentType?.limit_to_appointment_blocks
        ? `Finding dedicated ${appointmentType.name} slots…`
        : `Reading ${appointmentType?.name ?? "appointment"} availability…`,
      "Checking working hours & time off…",
      "Removing booked appointments…",
      `Fitting ${durationMinutes}-minute openings…`
    ],
    [
      appointmentType?.limit_to_appointment_blocks,
      appointmentType?.name,
      durationMinutes
    ]
  );

  const providerCount = activeSearch?.providers.length ?? providers.length;
  const loadingTitle =
    providerCount === 1
      ? "Checking 1 provider's schedule"
      : `Checking ${providerCount} providers' schedules`;

  const stillSearchingNames = useMemo(() => {
    if (!activeSearch) return "";
    return activeSearch.providers
      .filter((provider) => staffIdsStillSearching.includes(provider.id))
      .map((provider) => provider.name)
      .join(", ");
  }, [activeSearch, staffIdsStillSearching]);

  const withoutSlotsNames = useMemo(() => {
    if (!activeSearch) return "";
    return activeSearch.providers
      .filter((provider) => staffIdsWithoutSlots.includes(provider.id))
      .map((provider) => provider.name)
      .join(", ");
  }, [activeSearch, staffIdsWithoutSlots]);

  const earliestSlot = slots[0];
  const hasResultsSurface =
    Boolean(activeSearch) && (status === "extending" || status === "done");
  const searchDaysTotal = MAX_SEARCH_WINDOWS * SEARCH_WINDOW_DAYS;
  // On-screen results describe the search that RAN, not the fields as
  // currently edited — same reason activeSearch snapshots the criteria.
  const activeStartFrom = activeSearch?.criteria.startFrom ?? null;
  const searchWindowPhrase = activeStartFrom
    ? `${searchDaysTotal} days starting ${format(activeStartFrom, "MMM d, yyyy")}`
    : `next ${searchDaysTotal} days`;

  const handleDialogClose = useCallback(
    (_event: object, _reason: string) => onClose(),
    [onClose]
  );

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      title="Find next available appointment"
      subtitle={`Searching the next ${searchDaysTotal} days from ${
        startFrom ? format(startFrom, "MMM d, yyyy") : "today"
      }`}
      minWidth={DIALOG_MIN_WIDTH}
      dialogContentProps={{ sx: { pb: 0 } }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            // minmax(0, …) lets the results pane shrink below its content's
            // min width instead of blowing the dialog open sideways.
            md: `${CRITERIA_RAIL_WIDTH}px minmax(0, 1fr)`
          },
          columnGap: 3.5,
          rowGap: 2.5,
          pt: 1,
          pb: 2.5
        }}
      >
        <Stack
          spacing={2.5}
          useFlexGap
          sx={{
            borderRight: { md: 1 },
            borderColor: { md: "divider" },
            pr: { md: 3.5 }
          }}
        >
          <Autocomplete
            value={appointmentType}
            onChange={(_event, newValue) => onAppointmentTypeChange(newValue)}
            options={appointmentTypes}
            loading={isLoadingTypes}
            getOptionLabel={(option: AppointmentType) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option: AppointmentType) => (
              <ListItem {...props} key={option.id}>
                <Lens sx={{ color: option.color, fontSize: 12, mr: 1 }} />
                <ListItemText>
                  {option.name}
                  {option.default_duration && (
                    <Typography
                      component="span"
                      sx={{ ml: 1, fontSize: 12, color: "text.secondary" }}
                    >
                      (
                      {formatDurationValue(
                        option.default_duration.slice(0, -3)
                      )}
                      )
                    </Typography>
                  )}
                </ListItemText>
              </ListItem>
            )}
            renderInput={(params) => (
              <TextField {...params} label="Appointment type" required />
            )}
          />

          {/* Explicit sx: the field defaults to `{ ml: 2, mt: 1 }` when the
              prop is omitted, which would misalign it inside the rail. */}
          <ClinicFormField<FindNextAvailableFormValues>
            control={control}
            name="clinic"
            required
            sx={{ width: "100%" }}
          />

          <Box>
            <SelectProviderSimpleField
              multiple
              value={providers}
              label={`Providers (up to ${MAX_PROVIDERS})`}
              isActive
              clinicId={clinic?.id}
              groupName={
                appointmentType?.is_audiologists_only
                  ? GROUP_AUDIOLOGISTS
                  : undefined
              }
              onOptionSelected={handleProvidersSelected}
            />
            <Typography
              sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.5 }}
            >
              Showing providers who work at this clinic.
              {appointmentType?.is_audiologists_only &&
                ` ${appointmentType.name} is limited to audiologists.`}
            </Typography>
          </Box>

          <PatientFieldWithSearch
            control={control}
            fieldName="patient"
            label="Patient (optional)"
            onOptionSelected={setPatient}
          />

          <Box>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 600,
                color: "text.secondary",
                mb: 0.75
              }}
            >
              Start search from
            </Typography>
            <StartFromPresets
              value={startFromChoice}
              onChange={setStartFromChoice}
              allowCustom
            />
            {startFromChoice === "custom" && (
              <DateFormField<FindNextAvailableFormValues>
                control={control}
                name="start_from"
                label="Start date"
                disablePast
                sx={{ width: "100%", mt: 1.5 }}
              />
            )}
            {startFrom && (
              <Typography
                sx={{ fontSize: 11.5, color: "text.secondary", mt: 0.75 }}
                data-testid="find-next-available-start-hint"
              >
                Openings on or after {format(startFrom, "MMM d, yyyy")}
              </Typography>
            )}
          </Box>

          <Stack spacing={1} sx={{ mt: "auto", pt: 1 }}>
            <Button
              variant="contained"
              fullWidth
              disabled={!canSearch}
              onClick={handleSearch}
              data-testid="find-next-available-search"
            >
              Search
            </Button>
            <Button fullWidth onClick={onClose}>
              Cancel
            </Button>
          </Stack>
        </Stack>

        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            minHeight: { xs: 240, md: 400 },
            height: { md: RESULTS_PANE_HEIGHT }
          }}
        >
          {status === "idle" && (
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={0.5}
              sx={{ flex: 1, px: 4, textAlign: "center" }}
              data-testid="find-next-available-idle"
            >
              <Typography
                sx={{ fontSize: 14, fontWeight: 600 }}
                color="text.secondary"
              >
                Openings show up here
              </Typography>
              <Typography sx={{ fontSize: 13 }} color="text.secondary">
                Pick an appointment type, up to {MAX_PROVIDERS} providers, and a
                clinic, then search.
              </Typography>
            </Stack>
          )}
          {status === "searching" && <ResultsSkeleton />}

          {hasResultsSurface && activeSearch && (
            // useFlexGap so the terminal states below can center themselves in
            // the pane via `my: "auto"` — child margins lose to Stack's own
            // margin-based spacing otherwise.
            <Stack spacing={1.25} useFlexGap sx={{ flex: 1, minHeight: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
              >
                <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                  {slots.length > 0 ? (
                    <>
                      <b>
                        {slots.length} opening{slots.length === 1 ? "" : "s"}
                      </b>
                      {earliestSlot &&
                        ` · earliest ${formatSlotDayLabel(
                          new Date(earliestSlot.startTime)
                        )} · ${format(
                          new Date(earliestSlot.startTime),
                          "h:mmaaa"
                        )}`}
                    </>
                  ) : (
                    status === "extending" && "Searching…"
                  )}
                </Typography>
                {activeSearch.providers.length > 1 && slots.length > 0 && (
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={groupBy}
                    onChange={(_event, value) => value && setGroupBy(value)}
                  >
                    <ToggleButton value="time" sx={{ px: 1.5, py: 0.25 }}>
                      By time
                    </ToggleButton>
                    <ToggleButton value="provider" sx={{ px: 1.5, py: 0.25 }}>
                      By provider
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Stack>

              {status === "extending" && (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    bgcolor: "warning.light",
                    borderRadius: 1,
                    px: 1.5,
                    py: 1
                  }}
                  data-testid="find-next-available-extending"
                >
                  <CircularProgress size={14} color="inherit" />
                  <Typography sx={{ fontSize: 13 }}>
                    {`No openings yet for ${stillSearchingNames} — searching ${
                      searchedThroughDate
                        ? `${format(searchedThroughDate, "MMM d")} – ${format(
                            addDays(searchedThroughDate, SEARCH_WINDOW_DAYS),
                            "MMM d"
                          )}`
                        : "further out"
                    }…`}
                  </Typography>
                </Stack>
              )}

              {hasError && (
                <Stack
                  spacing={1}
                  alignItems="center"
                  // With no list below, center in the pane instead of leaving
                  // the error floating above a large void.
                  sx={{ py: 2, my: slots.length > 0 ? 0 : "auto" }}
                >
                  <Typography sx={{ fontSize: 13.5 }} color="error">
                    {slots.length > 0
                      ? "Something went wrong while searching further out — showing the openings found so far."
                      : "Something went wrong while searching for availability."}
                  </Typography>
                  <Button size="small" onClick={handleSearch}>
                    Try again
                  </Button>
                </Stack>
              )}

              {/* An extension-window failure must not hide the valid slots
                  window 1 already found — the list renders alongside the
                  error instead of being replaced by it. */}
              {slots.length > 0 && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    overflowY: "auto",
                    flex: { md: 1 },
                    minHeight: 0,
                    maxHeight: { xs: 340, md: "none" }
                  }}
                >
                  <SlotResultsList
                    slots={slots}
                    groupBy={groupBy}
                    providers={activeSearch.providers}
                    showSourceBadges={activeSearch.showSourceBadges}
                    onSelectSlot={handleSelectSlot}
                  />
                </Box>
              )}

              {!hasError && status === "done" && slots.length === 0 && (
                <Typography
                  sx={{
                    fontSize: 13.5,
                    textAlign: "center",
                    py: 2,
                    my: "auto"
                  }}
                  color="text.secondary"
                  data-testid="find-next-available-empty"
                >
                  No availability in the {searchWindowPhrase} for{" "}
                  {withoutSlotsNames || "the selected providers"}. Try different
                  providers or check their calendars for exceptions.
                </Typography>
              )}

              {!hasError &&
                status === "done" &&
                slots.length > 0 &&
                withoutSlotsNames && (
                  <Typography
                    sx={{ fontSize: 12.5 }}
                    color="text.secondary"
                    data-testid="find-next-available-partial-empty"
                  >
                    No availability in the {searchWindowPhrase} for{" "}
                    {withoutSlotsNames}.
                  </Typography>
                )}
            </Stack>
          )}

          {/* The bars animate in the selected providers' calendar colors so
              the mark reads as *this* search running, not a generic spinner.
              Shows only for the initial search: progressive month extensions
              keep already-valid results readable and use the inline banner. */}
          <LoadingOverlay
            open={showLoadingOverlay}
            title={loadingTitle}
            messages={loadingMessages}
            barColors={(activeSearch?.providers ?? []).map(
              (provider) => provider.color[400]
            )}
            progressAriaLabel="Searching for available appointment slots"
            maxWidth={340}
            data-testid="slot-search-loading-overlay"
          />
        </Box>
      </Box>
    </Dialog>
  );
}
